import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface MultiLineDataPoint {
  date: string
  values: Record<string, number>
}

interface LineConfig {
  label: string
  color: string
}

interface RechartsMultiLineChartProps {
  data: MultiLineDataPoint[]
  lines: Record<string, LineConfig>
  title: string
  height?: number
  formatValue?: (value: number) => string
  xAxisLabel?: string
  yAxisLabel?: string
}

export const RechartsMultiLineChart = ({
  data,
  lines,
  height = 200,
  formatValue = value => value.toLocaleString(),
  xAxisLabel,
  yAxisLabel,
}: RechartsMultiLineChartProps) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500 dark:text-[#AFAFAF]">
        No data available
      </div>
    )
  }

  // Transform data for Recharts
  const chartData = data.map(item => ({
    date: item.date,
    formattedDate: new Date(item.date).toLocaleDateString(),
    ...item.values,
  }))

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070] rounded-lg shadow-lg p-3">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            {new Date(label).toLocaleDateString()}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm font-medium" style={{ color: entry.color }}>
              {entry.name}: {formatValue(entry.value)}
            </p>
          ))}
        </div>
      )
    }

    return null
  }

  const CustomXAxisTick = ({ x, y, payload }: any) => {
    const date = new Date(payload.value)

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          className="text-xs text-gray-500 dark:text-[#AFAFAF]"
          dy={16}
          fill="currentColor"
          textAnchor="middle"
          x={0}
          y={0}
        >
          {date.toLocaleDateString()}
        </text>
      </g>
    )
  }

  const CustomYAxisTick = ({ x, y, payload }: any) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          className="text-xs text-gray-500 dark:text-[#AFAFAF]"
          dy={4}
          fill="currentColor"
          textAnchor="end"
          x={0}
          y={0}
        >
          {formatValue(payload.value)}
        </text>
      </g>
    )
  }

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex flex-wrap gap-4 mt-3 justify-center">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <div className="w-3 h-0.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-xs text-gray-600 dark:text-[#AFAFAF]">{entry.value}</span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Y-Axis Label */}
      {yAxisLabel && (
        <div className="flex justify-center mb-2">
          <span className="text-xs text-gray-600 dark:text-[#AFAFAF] font-medium">
            {yAxisLabel}
          </span>
        </div>
      )}

      <div style={{ height: height + 50 }}>
        <ResponsiveContainer height="100%" width="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 50,
            }}
          >
            <CartesianGrid
              className="text-gray-200 dark:text-gray-700"
              opacity={0.5}
              stroke="currentColor"
              strokeDasharray="3 3"
            />
            <XAxis
              axisLine={false}
              dataKey="date"
              interval="preserveStartEnd"
              tick={<CustomXAxisTick />}
              tickLine={false}
            />
            <YAxis axisLine={false} tick={<CustomYAxisTick />} tickLine={false} width={60} />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
            {Object.entries(lines).map(([key, config]) => (
              <Line
                key={key}
                dataKey={key}
                name={config.label}
                stroke={config.color}
                strokeWidth={2}
                type="monotone"
                activeDot={{
                  r: 4,
                  fill: config.color,
                  strokeWidth: 2,
                  stroke: '#fff',
                  style: {
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                  },
                }}
                dot={
                  data.length > 8
                    ? false
                    : {
                        strokeWidth: 1,
                        r: 2,
                      }
                }
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* X-Axis Label */}
      {xAxisLabel && (
        <div className="flex justify-center mt-2">
          <span className="text-xs text-gray-600 dark:text-[#AFAFAF] font-medium">
            {xAxisLabel}
          </span>
        </div>
      )}
    </div>
  )
}
